<script lang="ts">
  /**
   * The loading indicator, ported from the site's `shared/ui/Loader`.
   *
   * It is a clip-path animation rather than a spinning border, which is what gives it the segmented
   * sweep instead of the usual ring. The geometry is the site's; what changed is that the size and
   * colour are no longer hardcoded at 83px and #1c1b1f. At that size it can only ever be a page
   * loader, and this UI needs the same mark inside a button and beside a line of text.
   */
  let { size = "md", class: className = "" }: { size?: "sm" | "md" | "lg"; class?: string } = $props();

  const SIZES = {
    sm: "w-4 h-4 border-2",
    md: "w-8 h-8 border-4",
    lg: "w-14 h-14 border-[6px]",
  } as const;
</script>

<span class="inline-block rounded-full apibim-spinner {SIZES[size]} {className}" aria-hidden="true"></span>

<style>
  .apibim-spinner {
    aspect-ratio: 1;
    border-style: solid;
    border-color: currentColor;
    animation:
      apibim-spin-clip 0.8s infinite linear alternate,
      apibim-spin-turn 1.6s infinite linear;
  }

  /* Sweeps the visible wedge around the ring. */
  @keyframes apibim-spin-clip {
    0% {
      clip-path: polygon(50% 50%, 0 0, 50% 0%, 50% 0%, 50% 0%, 50% 0%, 50% 0%);
    }
    12.5% {
      clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 0%, 100% 0%, 100% 0%);
    }
    25% {
      clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 100%, 100% 100%, 100% 100%);
    }
    50% {
      clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%);
    }
    62.5% {
      clip-path: polygon(50% 50%, 100% 0, 100% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%);
    }
    75% {
      clip-path: polygon(50% 50%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 50% 100%, 0% 100%);
    }
    100% {
      clip-path: polygon(50% 50%, 50% 100%, 50% 100%, 50% 100%, 50% 100%, 50% 100%, 0% 100%);
    }
  }

  /* Flips and rotates underneath, so the wedge never repeats the same path twice. */
  @keyframes apibim-spin-turn {
    0% {
      transform: scaleY(1) rotate(0deg);
    }
    49.99% {
      transform: scaleY(1) rotate(135deg);
    }
    50% {
      transform: scaleY(-1) rotate(0deg);
    }
    100% {
      transform: scaleY(-1) rotate(-135deg);
    }
  }

  /* Someone who asked the operating system to stop moving things gets a static ring. */
  @media (prefers-reduced-motion: reduce) {
    .apibim-spinner {
      animation: none;
      clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%);
      opacity: 0.5;
    }
  }
</style>
